import { Layout } from "../../components/shared/Layout"
import { useRouter } from "next/router"
import React, { useEffect, useState } from "react"
import { addPageView, GameType, getGameDetails } from "../../services/games"
import { PageHead } from "../../components/shared/PageHead"
import useGlobalStore from "../../hooks/useGlobalStore"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { AcceptDebateModal } from "../../components/game-details/debate/AcceptDebateModal"
import { CreateSuccessModal } from "../../components/create-game/CreateSuccessModal"
import { GameInfo } from "../../components/game-details/shared/GameInfo"
import { GameBets } from "../../components/game-details/GameBets"
import { Comments } from "../../components/game-details/shared/Comments"
import { GameStatus } from "../../components/game-details/shared/GameStatus"
import { ProjectInfo } from "../../components/game-details/insurance/ProjectInfo"
import { GamePool } from "../../components/game-details/shared/GamePool"
import { twMerge } from "tailwind-merge"
import clsx from "clsx"
import { useAccount } from "wagmi"

export async function getServerSideProps({ locale, query, params, req }) {
    const { pid } = query

    const gameId = pid.toString().split("-").pop()
    const response = await getGameDetails(gameId)

    const gameDetails = response.data.data

    const { uniqueHash } = gameDetails

    try {
        addPageView(gameId)
            .then(function () {})
            .catch(function (ex) {
                console.log(ex)
            })
    } catch (e) {
        console.log(e)
    }

    return {
        props: {
            ...(await serverSideTranslations(locale, ["common", "game-details", "chat", "my-game", "game-create"])),
            game: response.data.data,
            pageUrl: `https://${req.headers.host}/game-details/${pid}`,
            shortPageUrl: `https://${req.headers.host}/s/${uniqueHash}`
        }
    }
}

function GameDetail(props) {
    const { address: account } = useAccount()
    const { user, isLoggedIn } = useGlobalStore()

    const router = useRouter()
    const { query } = router
    const { pid, accept, success, hash } = query

    const { game, pageUrl } = props
    const [gameDetails, setGameDetails] = useState(game)

    const { type: gameType, competitor } = gameDetails

    const { walletAddress: competitorWalletAddress } = competitor || {}

    const fetchData = async () => {
        if (!pid) {
            return
        }

        const gameId = pid.toString().split("-").pop()
        const response = await getGameDetails(gameId.toString())

        setGameDetails(response.data.data)
    }

    useEffect(() => {
        fetchData()
    }, [pid, account, user])

    const [isShowAcceptDebate, setIsShowAcceptDebate] = useState(false)
    const [isShowCreateSuccess, setIsShowCreateSuccess] = useState(false)
    const [transactionHash, setTransactionHash] = useState("")

    useEffect(() => {
        setIsShowAcceptDebate(
            isLoggedIn &&
                accept === "true" &&
                account &&
                competitorWalletAddress &&
                account.toLowerCase() === competitorWalletAddress.toLowerCase()
        )
        setIsShowCreateSuccess(isLoggedIn && success === "true")
        setTransactionHash(hash?.toString())
    }, [query, account, isLoggedIn])

    const closeCreateSuccessModal = async () => {
        setIsShowCreateSuccess(false)

        await router.replace(`/game-details/${pid}`, undefined, { shallow: true })
    }

    const onAccepted = async () => {
        await router.replace(`/game-details/${pid}`, undefined, { shallow: true })
    }

    const [selectedGamePool, setSelectedGamePool] = useState(0)
    const onChangeGamePool = (gamePool) => {
        setSelectedGamePool(gamePool)
    }

    const onClosePool = () => {
        setSelectedGamePool(0)
    }

    return (
        <div>
            <PageHead
                data={{
                    title: game.metaTitle,
                    description: game.metaDescription,
                    keyword: game.metaKeyword,
                    image: game.metaImage,
                    url: pageUrl
                }}
            />
            {isShowAcceptDebate && (
                <AcceptDebateModal
                    gameDetails={gameDetails}
                    isOpen={isShowAcceptDebate}
                    onRequestClose={() => setIsShowAcceptDebate(false)}
                    onAccepted={onAccepted}
                />
            )}
            {isShowCreateSuccess && (
                <CreateSuccessModal
                    gameDetails={gameDetails}
                    transactionHash={transactionHash}
                    isOpen={isShowCreateSuccess}
                    onRequestClose={closeCreateSuccessModal}
                    pageUrl={`${pageUrl}?accept=true`}
                />
            )}
            <Layout>
                <div
                    className={
                        "m-[10px_8px] flex max-w-[1232px] flex-col justify-between rounded-[20px] bg-[#F2F2F2] pt-[20px] lg:mx-auto lg:my-[50px] lg:flex-row lg:rounded-[10px] lg:pt-0"
                    }
                >
                    <div className={"flex-1"}>
                        <div id={"div-game-info"} className={"px-[16px] pt-[40px] lg:pt-[29px] lg:pl-[25px] lg:pr-9"}>
                            <GameInfo game={gameDetails} />
                        </div>
                        <div
                            className={
                                "mt-[34px] hidden border-t border-[#ADADAD] pb-[29px] lg:ml-[25px] lg:mr-9 lg:block"
                            }
                        >
                            <Comments gameDetails={gameDetails} />
                        </div>
                    </div>

                    <div className={"lg:w-[340px]"}>
                        {gameType === GameType.INSURANCE && (
                            <div className={"mb-3"}>
                                <ProjectInfo game={gameDetails} />
                            </div>
                        )}
                        <div className={"hidden lg:block"}>
                            <GameStatus game={gameDetails} fetchData={fetchData} />
                        </div>
                        <div className={"lg:mt-5"}>
                            <GameBets game={gameDetails} mode={"mobile"} />
                        </div>
                        <div className={"mt-[34px] border-t border-[#ADADAD] lg:hidden"}>
                            <Comments gameDetails={gameDetails} />
                        </div>
                    </div>
                </div>
                {/*Mobile view*/}
                <GamePool game={game} onChangeGamePool={onChangeGamePool} />
                <div
                    className={twMerge(
                        clsx(
                            "sticky bottom-0 z-40 h-0 w-full overflow-hidden bg-white transition-all duration-500",
                            selectedGamePool && "h-[100vh]"
                        )
                    )}
                >
                    <GameStatus
                        game={gameDetails}
                        fetchData={fetchData}
                        defaultPool={selectedGamePool}
                        onClosePool={onClosePool}
                    />
                </div>
            </Layout>
        </div>
    )
}

export default GameDetail
